import Phaser from "phaser";
import Game from "../game/Game";
import { Label, Sizer } from "phaser3-rex-plugins/templates/ui/ui-components";
import Scenes from "../enums/Scenes";
import ApiService from "../../handlers/apiService";
import { BoostTypeEnum, UserBoost, UserBoosts } from "../../handlers/types";
import { openInvoice } from "@telegram-apps/sdk";
import { TEXT_STYLES } from "../../utils/FontManager";
import { t } from "../../utils/i18n/index";
import { useGameStore } from "../../store/gameStore";
import { showEnergyPopup } from "../../utils/modal/ModalManager";
import { DEFAULT_SCALE } from "../consts/UI";
import StageService from "../../services/StageService";
import StageType from "../enums/StageType";
import { SPRITESHEET_KEYS, SPRITESHEET_FRAMES } from "../consts/SPRITESHEETS";

export default class GameOverScene extends Phaser.Scene {
  private panel: Sizer;
  private mask: Phaser.GameObjects.Graphics;
  private scaleRatio: number;
  private coins = 0;
  private container: Phaser.GameObjects.Container;
  private hasReviveBoost: boolean = false;
  private reviveBoostPrice: number = 64; // Default price, will be updated from API
  private hasDoublingBoost: boolean = false; // Track doubling boost availability
  private doublingBoostPrice: number = 10; // Default price for doubling boost, will be updated from API
  private hasEnergy: boolean = false;
  private reviveButton: Label | null = null;
  private doubleCoinsButton: Label | null = null; // New double coins button
  private repeatButton: Label | null = null;
  private objectives: {
    description?: string;
    completed: boolean;
    prize: number;
    descriptionKey?: string;
  }[] = [];
  private isLevelFinished: boolean = false; // New property to track level completion
  private endgameReward: number = 0;
  private confettiEmitter: Phaser.GameObjects.Container | null = null;
  private confettiColors: number[] = [
    0xffd700, 0xff4500, 0x00bfff, 0x32cd32, 0xff69b4, 0xba55d3, 0xffa500,
  ];

  constructor() {
    super({ key: Scenes.GameOverScene });
  }

  async init(data: {
    coins: number;
    objectives?: {
      description?: string;
      completed: boolean;
      prize: number;
      descriptionKey?: string;
    }[];
    isLevelFinished?: boolean;
    endgameReward?: number;
  }) {
    this.coins = data.coins;
    this.scaleRatio = DEFAULT_SCALE;
    this.hasEnergy = useGameStore.getState().hasEnergy();
    this.isLevelFinished = data.isLevelFinished || false;
    this.endgameReward = data.endgameReward || 0; // Store the endgame reward

    // Store objectives if provided
    if (data.objectives) {
      this.objectives = data.objectives;
    }

    const response = await ApiService.getInstance().api.boostsList();

    await this.updateReviveBoost(response.boosts);
    await this.updateDoublingBoost(response.boosts); // Check for doubling boost
  }

  create() {
    // First stop all existing music and sounds
    this.sound.stopAll();

    // Play game over sound using pooled audio (sound settings handled by facade)
    if (this.isLevelFinished) {
      Game.Core.poolManager?.playLevelUpMusic();
    } else {
      Game.Core.poolManager?.playGameOverSound();
    }

    const BASE_DEPTH = 1000;

    // Layer 1: Background mask
    this.mask = this.add
      .graphics()
      .setScrollFactor(0)
      .fillStyle(0x000000, 0.8)
      .fillRect(0, 0, this.cameras.main.width, this.cameras.main.height)
      .setDepth(BASE_DEPTH);

    // Layer 2: Add confetti if level is finished
    if (this.isLevelFinished && this.endgameReward > 0) {
      this.createConfetti(BASE_DEPTH + 1);
    }

    // Layer 3: Create container for all UI elements
    this.container = this.add.container(0, 0).setDepth(BASE_DEPTH + 10);

    const dialogWidth = 345 * this.scaleRatio;
    const dialogHeight = 377 * this.scaleRatio;

    if (!this.rexUI || !this.rexUI.add) {
      throw new Error("RexUI not available for panel creation");
    }

    this.panel = this.rexUI.add
      .sizer({
        x: this.cameras.main.width / 2,
        y: this.cameras.main.height / 2,
        width: dialogWidth,
        height: dialogHeight,
        orientation: "vertical",
        anchor: {
          centerX: "center",
          centerY: "center",
        },
        space: {
          left: 20 * this.scaleRatio,
          right: 20 * this.scaleRatio,
          top: 30 * this.scaleRatio,
          bottom: 20 * this.scaleRatio,
          item: 10 * this.scaleRatio,
        },
      })
      .setMinHeight(dialogHeight);

    if (!this.panel) {
      throw new Error("Failed to create panel");
    }

    // Create background with rounded corners
    const panelBackground = this.rexUI.add.roundRectangle(
      0,
      0,
      0,
      0,
      20 * this.scaleRatio,
      0x1e2938
    );

    // Add the background to the panel
    this.panel.addBackground(panelBackground);

    // If level is finished, add animated stroke using rexUI
    if (this.isLevelFinished && this.endgameReward > 0) {
      // Create array of colors for animation
      const colors = [
        0xffd700, 0xff4500, 0x00bfff, 0x32cd32, 0xff69b4, 0xba55d3, 0xffa500,
      ];
      let colorIndex = 0;

      // Create initial stroke with first color
      const stroke = this.rexUI.add
        .roundRectangle(0, 0, 0, 0, 20 * this.scaleRatio)
        .setStrokeStyle(3 * this.scaleRatio, colors[0]);

      // Add the stroke as a background (before the main background)
      this.panel.addBackground(stroke);
      this.panel.addBackground(panelBackground);

      // Animate the stroke color
      this.time.addEvent({
        delay: 500,
        callback: () => {
          colorIndex = (colorIndex + 1) % colors.length;
          stroke.setStrokeStyle(3 * this.scaleRatio, colors[colorIndex]);
        },
        callbackScope: this,
        loop: true,
      });
    } else {
      // Regular static stroke for game over
      panelBackground.setStrokeStyle(2 * this.scaleRatio, 0x141c27);
      this.panel.addBackground(panelBackground);
    }

    // Title with skull icons (only for game over, not for level finished)
    const titleContainer = this.rexUI.add.sizer({
      orientation: "horizontal",
      space: { item: 10 * this.scaleRatio },
    });

    if (!this.isLevelFinished) {
      // Only add skull icons if level is not finished
      const skullIcon = this.add
        .image(0, 0, SPRITESHEET_KEYS.PICKUPS, SPRITESHEET_FRAMES.PICKUPS.SKULL)
        .setDisplaySize(24 * this.scaleRatio, 24 * this.scaleRatio);

      titleContainer.add(skullIcon, {
        align: "center",
      });
    }

    // Change title based on level completion status
    const titleText = this.add.text(
      0,
      0,
      this.isLevelFinished ? t("game.levelFinished") : t("game.gameOver"),
      {
        ...TEXT_STYLES.PIXEL,
        fontSize: `${22 * this.scaleRatio}px`,
        color: "#E58C0D",
        wordWrap: { width: dialogWidth - 120 * this.scaleRatio },
        align: "center",
      }
    );

    titleContainer.add(titleText, {
      align: "center",
    });

    if (!this.isLevelFinished) {
      // Only add second skull icon if level is not finished
      const skullIcon2 = this.add
        .image(0, 0, SPRITESHEET_KEYS.PICKUPS, SPRITESHEET_FRAMES.PICKUPS.SKULL)
        .setDisplaySize(24 * this.scaleRatio, 24 * this.scaleRatio);

      titleContainer.add(skullIcon2, {
        align: "center",
      });
    }

    this.panel.add(titleContainer, {
      padding: { bottom: 10 * this.scaleRatio },
    });

    // Coins and Time Row (horizontal layout) - MOVED UP
    const statsContainer = this.rexUI.add.sizer({
      orientation: "horizontal",
      space: { item: 20 * this.scaleRatio },
    });

    // Coins display
    const coinsContainer = this.rexUI.add.sizer({
      orientation: "horizontal",
      space: { item: 10 * this.scaleRatio },
    });

    const coinsText = this.add.text(0, 0, this.coins.toString(), {
      ...TEXT_STYLES.PIXEL,
      fontSize: `${42 * this.scaleRatio}px`,
      color: "#FFFFFF",
    });

    const coinIcon = this.add
      .image(0, 0, SPRITESHEET_KEYS.PICKUPS, SPRITESHEET_FRAMES.PICKUPS.COIN)
      .setDisplaySize(48 * this.scaleRatio, 48 * this.scaleRatio);

    coinsContainer.add(coinsText).add(coinIcon);
    statsContainer.add(coinsContainer);

    this.panel.add(statsContainer, {
      padding: { bottom: 10 * this.scaleRatio },
    });

    // Add bonus text if level is finished and there's an endgame reward
    if (this.isLevelFinished && this.endgameReward > 0) {
      const bonusText = this.add.text(
        0,
        0,
        t("game.levelSurvivalBonus", { amount: this.endgameReward }),
        {
          ...TEXT_STYLES.PIXEL,
          fontSize: `${12 * this.scaleRatio}px`,
          color: "#FFFFFF",
          wordWrap: { width: dialogWidth * 0.7 }, // 70% of dialog width
          lineSpacing: 5 * this.scaleRatio, // Add more gap between lines
          align: "center", // Center align the text if it wraps
        }
      );
      this.panel.add(bonusText, {
        padding: { bottom: 10 * this.scaleRatio },
      });
    }

    // Add empty space to push buttons to bottom
    this.panel.add(this.rexUI.add.sizer(), {
      proportion: 1,
    });

    // Buttons
    const buttonWidth = dialogWidth - 40 * this.scaleRatio;
    const buttonHeight = 49 * this.scaleRatio;

    // Add Double Coins button (only for level completed)
    if (this.isLevelFinished) {
      try {
        this.doubleCoinsButton = this.createButton(
          `${t("game.doubleCoins")} (${this.doublingBoostPrice}     )`,
          0xffb935,
          buttonWidth,
          buttonHeight,
          SPRITESHEET_FRAMES.PICKUPS.STAR, // Always create the star icon, visibility will be controlled in update methods
          this.coins <= 0, // Disable button if coins are 0 or negative
          true
        ).onClick(async () => {
          if (this.coins <= 0) return; // Do nothing if offer expired or no coins

          try {
            if (this.hasDoublingBoost) {
              // Use doubling boost if available
              const response =
                await ApiService.getInstance().api.boostsUseCreate({
                  boostType: BoostTypeEnum.Doubling,
                  runId: StageService.getInstance().getRunId(),
                });

              if (response.success) {
                // Double the coins with animation
                this.doubleCoins();

                // Disable the button after use
                const background = this.doubleCoinsButton?.getElement(
                  "background"
                ) as Phaser.GameObjects.Shape;
                if (background) {
                  background.setFillStyle(0x666666);
                }
                this.doubleCoinsButton?.removeInteractive();
              }
            } else {
              // If no boost available, get an invoice
              const { invoiceUrl } =
                await ApiService.getInstance().api.boostsBuyCreate({
                  boostType: BoostTypeEnum.Doubling,
                });

              if (invoiceUrl && openInvoice.isAvailable()) {
                const status = await openInvoice(invoiceUrl, "url");

                if (status === "paid") {
                  this.createConfetti(BASE_DEPTH + 1);

                  // Double the coins after payment
                  this.doubleCoins();

                  // Wait for 1 second before using the boost
                  await new Promise((resolve) => setTimeout(resolve, 1000));

                  // Use the boost right after purchase
                  await ApiService.getInstance().api.boostsUseCreate({
                    boostType: BoostTypeEnum.Doubling,
                    runId: StageService.getInstance().getRunId(),
                  });

                  // Update boost status
                  await this.updateDoublingBoost();

                  // Disable the button after use
                  const background = this.doubleCoinsButton?.getElement(
                    "background"
                  ) as Phaser.GameObjects.Shape;
                  if (background) {
                    background.setFillStyle(0x666666);
                  }
                  this.doubleCoinsButton?.removeInteractive();
                }
              }
            }
          } catch (error) {
            console.error("Failed to handle doubling boost:", error);
          }
        });
      } catch (error) {
        console.error("Failed to create double coins button:", error);
        this.doubleCoinsButton = null;
      }
    }

    // Only show revive button if level is not finished
    if (!this.isLevelFinished) {
      try {
        this.reviveButton = this.createButton(
          `${t("game.revive")} (${this.reviveBoostPrice}     )`,
          0x509e3a,
          buttonWidth,
          buttonHeight,
          SPRITESHEET_FRAMES.PICKUPS.STAR, // Always create the star icon, visibility will be controlled in update methods
          false,
          true
        ).onClick(async () => {
          if (this.hasReviveBoost) {
            try {
              // Use revive boost
              await ApiService.getInstance().api.boostsUseCreate({
                boostType: BoostTypeEnum.Revive,
                runId: StageService.getInstance().getRunId(),
              });
              this.revivePlayer();
            } catch (error) {
              console.error("Failed to use revive boost:", error);
            }
          } else {
            try {
              // Try to purchase a revive boost
              const { invoiceUrl } =
                await ApiService.getInstance().api.boostsBuyCreate({
                  boostType: BoostTypeEnum.Revive,
                });

              if (invoiceUrl && openInvoice.isAvailable()) {
                const status = await openInvoice(invoiceUrl, "url");
                if (status === "paid") {
                  this.revivePlayer();

                  // Wait for 1 second before using the boost
                  await new Promise((resolve) => setTimeout(resolve, 1000));

                  await ApiService.getInstance().api.boostsUseCreate({
                    boostType: BoostTypeEnum.Revive,
                    runId: StageService.getInstance().getRunId(),
                  });

                  await this.updateReviveBoost();
                }
              }
            } catch (error) {
              console.error("Failed to get invoice link:", error);
            }
          }
        });
      } catch (error) {
        console.error("Failed to create revive button:", error);
        this.reviveButton = null;
      }
    }

    // Check if all objectives are completed
    const allObjectivesCompleted =
      this.objectives.length > 0 &&
      this.objectives.every((objective) => objective.completed);

    const energyIconTexture = this.hasEnergy ? "energy_full" : "energy_empty";

    // Determine the text for the retry/next button based on objectives completion
    const buttonText = allObjectivesCompleted
      ? t("common.nextLevel")
      : this.hasEnergy
      ? t("common.retry")
      : t("game.no_energy_retry");

    this.repeatButton = this.createButton(
      buttonText,
      0x248bda,
      buttonWidth,
      buttonHeight,
      energyIconTexture
    ).onClick(() => {
      this.hasEnergy = useGameStore.getState().hasEnergy();

      if (!this.hasEnergy) {
        showEnergyPopup(() => {
          // This callback is called when the popup actually closes
          this.updateRepeatButton();

          // Check if energy was purchased and start the game if so
          this.hasEnergy = useGameStore.getState().hasEnergy();
          if (this.hasEnergy) {
            this.startGame(allObjectivesCompleted);
          }
        });
        return;
      }

      this.startGame(allObjectivesCompleted);
    });

    const doneButton = this.createButton(
      t("common.goHome"),
      0x2e354a,
      buttonWidth,
      buttonHeight
    ).onClick(() => {
      // Get reference to the main scene
      const mainScene = this.scene.get(Scenes.MainScene);

      // Call destroy method directly to ensure music cleanup
      if (mainScene && typeof mainScene["destroy"] === "function") {
        mainScene["destroy"]();
      }

      Game.Core.SceneManager.FinishGameFromGameOver();
    });

    // Add Double Coins button only if level is finished and button was successfully created
    if (this.isLevelFinished && this.doubleCoinsButton && this.panel) {
      try {
        this.panel.add(this.doubleCoinsButton);
      } catch (error) {
        console.error("Failed to add double coins button to panel:", error);
      }
    }

    // Add objectives section
    const separator = this.add.rectangle(
      0,
      0,
      dialogWidth * 0.8,
      1 * this.scaleRatio,
      0xffffff,
      0.3
    );

    this.panel.add(separator, {
      padding: { top: 15 * this.scaleRatio, bottom: 15 * this.scaleRatio },
      align: "center",
    });

    // Add objectives title
    const objectivesTitle = this.add.text(0, 0, t("common.objectives"), {
      ...TEXT_STYLES.REGULAR,
      fontSize: `${15 * this.scaleRatio}px`,
      color: "#84919E",
    });

    this.panel.add(objectivesTitle, {
      padding: { bottom: 2 * this.scaleRatio },
      align: "left",
    });

    // Add each objective
    this.objectives.forEach((objective, index) => {
      const completed = objective.completed;
      const prefix = completed ? "✓" : "○";

      // Use the objective's descriptionKey for translation, fallback to direct description
      const displayText = objective.descriptionKey
        ? t(objective.descriptionKey)
        : objective.description || "Unknown objective";

      const objectiveContainer = this.rexUI.add.sizer({
        orientation: "horizontal",
        space: { item: 5 * this.scaleRatio },
      });

      // Create the objective text
      const objectiveText = this.add.text(0, 0, `${prefix} ${displayText}`, {
        ...TEXT_STYLES.REGULAR,
        fontSize: `${14 * this.scaleRatio}px`,
        color: completed ? "#57AF3A" : "#FFFFFF",
        lineSpacing: -2 * this.scaleRatio,
      });

      objectiveContainer.add(objectiveText);

      // Add negative top padding for all items except the first one to reduce spacing between items
      const itemPadding =
        index > 0 ? { top: -2 * this.scaleRatio, bottom: 0 } : { bottom: 0 };

      this.panel.add(objectiveContainer, {
        padding: itemPadding,
        align: "left",
      });
    });

    // Add padding after objectives list
    this.panel.add(this.rexUI.add.sizer().setMinHeight(10 * this.scaleRatio));

    // Only add revive button container if level is not finished and button was successfully created
    if (!this.isLevelFinished && this.reviveButton && this.panel) {
      try {
        this.panel.add(this.reviveButton);
      } catch (error) {
        console.error("Failed to add revive button to panel:", error);
      }
    }

    // Add the remaining buttons
    this.panel.add(this.repeatButton).add(doneButton);

    this.panel.layout();
    this.container.add(this.panel);
  }

  private revivePlayer() {
    Game.Core.Player.isDead = false;
    Game.Core.Player.Body.enable = true;
    Game.Core.Player.RecoverHp(Game.Core.Player.maxHp * 0.5);
    Game.Core.Player.SetInvulForMilliSeconds(2000);

    // Restore game objects that were destroyed during GameOver
    Game.Core.restoreGameObjects();

    this.scene.stop(Scenes.GameOverScene);

    // Resume MainScene without consuming energy (player already paid for revive)
    const mainScene = this.scene.get(Scenes.MainScene) as Phaser.Scene & {
      init: (data?: any) => void;
    };
    if (mainScene && mainScene.init) {
      mainScene.init({ consumeEnergy: false });
    }
    this.scene.resume(Scenes.MainScene);

    // Create fresh music instance for the main scene if music enabled
    if (Game.Core?.PlayerOptions?.MusicEnabled !== false) {
      const music = this.sound.add("desert_music", { volume: 0.4, loop: true });
      music.play();
    }
  }

  private updateReviveButton() {
    if (!this.reviveButton) return;

    // Update the button text with the boost price
    const newText = this.hasReviveBoost
      ? `${t("game.revive")}`
      : `${t("game.revive")} (${this.reviveBoostPrice}     )`;

    // Update button text
    this.reviveButton.setText(newText);

    // Get the container (which is the 'text' property of the button Label)
    const contentContainer = this.reviveButton.getElement(
      "text"
    ) as Phaser.GameObjects.Container;

    // Hide/show icon based on boost availability
    if ((contentContainer?.list?.length ?? 0) > 1) {
      const icon = contentContainer.list[1] as Phaser.GameObjects.Image;
      if (icon) {
        // Hide icon if boost is available, show if not available
        icon.setVisible(!this.hasReviveBoost);
      }
    }
  }

  private async updateReviveBoost(userBoosts?: UserBoost[]) {
    try {
      if (userBoosts) {
        const reviveBoost = userBoosts.find(
          (boost) => boost.type === BoostTypeEnum.Revive
        );
        this.hasReviveBoost = (reviveBoost?.count ?? 0) > 0;
        this.reviveBoostPrice = reviveBoost?.price ?? 0;
      } else {
        const response = await ApiService.getInstance().api.boostsList();
        // Find revive boost in the boosts array
        const reviveBoost = response.boosts.find(
          (boost) => boost.type === BoostTypeEnum.Revive
        );

        this.hasReviveBoost = (reviveBoost?.count ?? 0) > 0;
        this.reviveBoostPrice = reviveBoost?.price ?? 0;
      }
      // Only update button if it exists (avoids redundant calls during init)
      if (this.reviveButton) {
        this.updateReviveButton();
      }
    } catch (error) {
      console.error("Failed to get boosts:", error);
      this.hasReviveBoost = false;
      // Only update button if it exists (avoids redundant calls during init)
      if (this.reviveButton) {
        this.updateReviveButton();
      }
    }
  }

  private async updateDoublingBoost(userBoosts?: UserBoost[]) {
    try {
      if (userBoosts) {
        const doublingBoost = userBoosts.find(
          (boost) => boost.type === BoostTypeEnum.Doubling
        );
        this.hasDoublingBoost = (doublingBoost?.count || 0) > 0;
        this.doublingBoostPrice = doublingBoost?.price || 0;
      } else {
        const response = await ApiService.getInstance().api.boostsList();
        // Find doubling boost in the boosts array
        const doublingBoost = response.boosts.find(
          (boost) => boost.type === BoostTypeEnum.Doubling
        );

        this.hasDoublingBoost = (doublingBoost?.count || 0) > 0;
        this.doublingBoostPrice = doublingBoost?.price ?? 0;
      }
      // Only update button if it exists (avoids redundant calls during init)
      if (this.doubleCoinsButton) {
        this.updateDoublingButton();
      }
    } catch (error) {
      console.error("Failed to get doubling boost:", error);
      this.hasDoublingBoost = false;
      // Only update button if it exists (avoids redundant calls during init)
      if (this.doubleCoinsButton) {
        this.updateDoublingButton();
      }
    }
  }

  private updateDoublingButton() {
    if (!this.doubleCoinsButton) return;

    // Update the button text with the boost price
    const newText = this.hasDoublingBoost
      ? `${t("game.doubleCoins")}`
      : `${t("game.doubleCoins")} (${this.doublingBoostPrice}     )`;

    // Update button text
    this.doubleCoinsButton.setText(newText);

    // Get the container (which is the 'text' property of the button Label)
    const contentContainer = this.doubleCoinsButton.getElement(
      "text"
    ) as Phaser.GameObjects.Container;

    // Hide/show icon based on boost availability
    if ((contentContainer?.list?.length ?? 0) > 1) {
      const icon = contentContainer.list[1] as Phaser.GameObjects.Image;
      if (icon) {
        // Hide icon if boost is available, show if not available
        icon.setVisible(!this.hasDoublingBoost);
      }
    }
  }

  private doubleCoins() {
    // Store original value for animation
    const originalCoins = this.coins;

    // Double the coins
    this.coins *= 2;

    // Animate the coins increasing
    const statsContainer = this.panel.getElement("items")?.[1] as Sizer;

    if (statsContainer) {
      const coinsContainer = statsContainer.getElement("items")?.[0] as Sizer;

      if (coinsContainer) {
        const coinsText = coinsContainer.getElement(
          "items"
        )?.[0] as Phaser.GameObjects.Text;

        if (coinsText) {
          // Animate counter from original to doubled value
          let current = originalCoins;
          const target = this.coins;
          const duration = 1500; // 1.5 seconds
          const step = Math.max(1, Math.floor((target - current) / 30)); // At least 30 steps

          const updateCounter = () => {
            current = Math.min(current + step, target);
            coinsText.setText(current.toString());

            // Trigger layout update to adjust coin icon position after text change
            coinsContainer.layout();
            statsContainer.layout();
            this.panel.layout();

            if (current < target) {
              this.time.delayedCall(duration / 30, updateCounter);
            }
          };

          updateCounter();
        }
      }
    }
  }

  private createButton(
    text: string,
    color: number,
    width: number,
    height: number,
    iconKey?: string,
    isInactive: boolean = false,
    withPrice: boolean = false
  ): Label {
    if (!this.rexUI || !this.rexUI.add) {
      throw new Error("RexUI not available for button creation");
    }

    const background = this.rexUI.add.roundRectangle(
      0,
      0,
      width,
      height,
      10 * this.scaleRatio,
      color
    );

    // Only apply inactive styling if it's not the retry button with no energy
    const isEnergyRetryButton = text === t("game.no_energy_retry");
    const shouldApplyInactiveStyle = isInactive && !isEnergyRetryButton;

    if (shouldApplyInactiveStyle) {
      background.setFillStyle(0x666666);
    }

    const buttonTextColorLight = shouldApplyInactiveStyle
      ? "#999999"
      : "#FFFFFF";
    const buttonTextColor =
      color === 0xffb935 ? "#171F2A" : buttonTextColorLight;

    const contentContainer = this.add.container(0, 0);
    // Simple button with just text
    const buttonText = this.add
      .text(0, 0, text, {
        ...TEXT_STYLES.HEADER,
        fontSize: `${16 * this.scaleRatio}px`,
        color: buttonTextColor,
      })
      .setOrigin(0.5, 0.5);

    contentContainer.add(buttonText);

    if (iconKey) {
      let icon: Phaser.GameObjects.Image;

      // Check if it's a spritesheet frame (star)
      if (iconKey === SPRITESHEET_FRAMES.PICKUPS.STAR) {
        icon = this.add
          .image(
            buttonText.width / 2 + (withPrice ? -23 : 5) * this.scaleRatio,
            buttonText.y,
            SPRITESHEET_KEYS.PICKUPS,
            iconKey
          )
          .setDisplaySize(16 * this.scaleRatio, 16 * this.scaleRatio)
          .setOrigin(0, 0.5);

        icon.setTintFill(buttonTextColor === "#171F2A" ? 0x171f2a : 0xffffff);
      } else {
        // Regular texture (like energy icons)
        icon = this.add
          .image(
            buttonText.width / 2 + (withPrice ? -23 : 5) * this.scaleRatio,
            buttonText.y,
            iconKey
          )
          .setDisplaySize(16 * this.scaleRatio, 16 * this.scaleRatio)
          .setOrigin(0, 0.5);
      }

      contentContainer.add(icon);
    }

    // Create and return the button with correct type
    const button = this.rexUI.add.label({
      background,
      text: contentContainer,
      space: {
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
      },
      align: "center",
      width: width,
      height: height,
    }) as unknown as Label;

    if (!shouldApplyInactiveStyle) {
      button.setInteractive();
    }

    // Add a custom setText method to the button
    button.setText = (newText: string) => {
      if (contentContainer?.list?.length > 0) {
        const textObj = contentContainer.list[0] as Phaser.GameObjects.Text;
        if (textObj && textObj.setText) {
          textObj.setText(newText);

          // If there's an icon, reposition it after text change
          if (contentContainer.list.length > 1) {
            const icon = contentContainer.list[1] as Phaser.GameObjects.Image;
            if (icon) {
              icon.setPosition(
                buttonText.width / 2 + (withPrice ? -23 : 5) * this.scaleRatio,
                buttonText.y
              );
              // Update tint to match text color
              const textColor = textObj.style.color;
              icon.setTintFill(
                textColor === "#171F2A" || textColor === "#171f2a"
                  ? 0x171f2a
                  : 0xffffff
              );
            }
          }
        }
      }
      return button;
    };

    return button;
  }

  // Enhanced confetti effect with much slower movement
  createConfetti(depth: number) {
    // Create container for confetti pieces
    const confettiContainer = this.add.container(0, 0);
    confettiContainer.setDepth(depth);

    // Create a continuous confetti emitter
    const emitConfetti = () => {
      // Create 80 confetti pieces per burst
      for (let i = 0; i < 80; i++) {
        const confetti = this.add.graphics();
        const color = Phaser.Utils.Array.GetRandom(this.confettiColors);
        confetti.fillStyle(color, 1);

        // All confetti are circles
        const size = Phaser.Math.Between(8, 20) * this.scaleRatio;
        confetti.fillCircle(0, 0, size / 2);

        // Start from center of screen
        const centerX = this.cameras.main.width / 2;
        const centerY = this.cameras.main.height / 2;
        confetti.x = centerX + Phaser.Math.Between(-10, 10);
        confetti.y = centerY + Phaser.Math.Between(-10, 10);

        // More exciting animation - wider angle range for more spread
        const angle = Phaser.Math.Between(-180, 0); // Full top half spread

        // Reduce distance for slower perceived movement
        const distance =
          Phaser.Math.Between(
            this.cameras.main.height * 0.4,
            this.cameras.main.height * 0.8
          ) * this.scaleRatio;

        // Much slower animation (significantly increased duration)
        const duration = Phaser.Math.Between(3000, 5000); // Dramatically increased

        // Add minimal rotation for gentle floating
        const rotation = Phaser.Math.Between(-1, 1); // Minimal rotation

        // Explosive animation with gentler movement
        this.tweens.add({
          targets: confetti,
          x: confetti.x + distance * Math.cos((angle * Math.PI) / 180),
          y: confetti.y + distance * Math.sin((angle * Math.PI) / 180),
          rotation: rotation,
          scaleX: { from: 1, to: 0.5 },
          scaleY: { from: 1, to: 0.5 },
          alpha: { from: 1, to: 0 },
          duration: duration,
          ease: "Cubic.easeOut",
        });

        confettiContainer.add(confetti);
      }
    };

    // Initial burst
    emitConfetti();

    // Continue emitting confetti every 300ms for longer
    const timer = this.time.addEvent({
      delay: 300,
      callback: emitConfetti,
      callbackScope: this,
      repeat: 16, // ~5 seconds total
    });

    this.confettiEmitter = confettiContainer;
  }

  // Clean up confetti when scene is shut down
  cleanupConfetti() {
    if (this.confettiEmitter) {
      this.confettiEmitter.destroy();
      this.confettiEmitter = null;
    }
  }

  // Clean up when scene is shut down
  shutdown() {
    this.cleanupConfetti();
  }

  // Clean up when scene is destroyed
  destroy() {
    this.cleanupConfetti();
  }

  // Helper method to get the next stage ID using StageType enum
  private getNextStageId(currentStageId: StageType): StageType | null {
    // Get all enum values in an array
    const stageValues = Object.values(StageType);

    // Find the current index
    const currentIndex = stageValues.indexOf(currentStageId);

    // Return the next stage if it exists
    if (currentIndex !== -1 && currentIndex < stageValues.length - 1) {
      return stageValues[currentIndex + 1];
    }

    return null; // No next stage found
  }

  private startGame(allObjectivesCompleted: boolean) {
    // If all objectives are completed, go to next level
    if (allObjectivesCompleted) {
      // Get the next stage ID
      const currentStageId = Game.Core.PlayerOptions.SelectedStage;
      const nextStageId = this.getNextStageId(currentStageId);

      if (nextStageId) {
        // Set the next stage as selected
        Game.Core.PlayerOptions.SelectedStage = nextStageId;

        // Get reference to the main scene
        const mainScene = this.scene.get(Scenes.MainScene);

        // Call destroy method directly to ensure music cleanup
        if (mainScene && typeof mainScene["destroy"] === "function") {
          mainScene["destroy"]();
        }

        // Navigate to the next stage
        Game.Core.SceneManager.ResumeFromGameOver();
      } else {
        // Get reference to the main scene
        const mainScene = this.scene.get(Scenes.MainScene);

        // Call destroy method directly to ensure music cleanup
        if (mainScene && typeof mainScene["destroy"] === "function") {
          mainScene["destroy"]();
        }

        Game.Core.SceneManager.ResumeFromGameOver();
      }
    } else {
      // Get reference to the main scene
      const mainScene = this.scene.get(Scenes.MainScene);

      // Call destroy method directly to ensure music cleanup
      if (mainScene && typeof mainScene["destroy"] === "function") {
        mainScene["destroy"]();
      }

      Game.Core.SceneManager.ResumeFromGameOver();
    }
  }

  private updateRepeatButton() {
    if (!this.repeatButton) return;

    this.hasEnergy = useGameStore.getState().hasEnergy();

    // Check if all objectives are completed
    const allObjectivesCompleted =
      this.objectives.length > 0 &&
      this.objectives.every((objective) => objective.completed);

    const buttonText = allObjectivesCompleted
      ? t("common.nextLevel")
      : this.hasEnergy
      ? t("common.retry")
      : t("game.no_energy_retry");

    const energyIconTexture = this.hasEnergy ? "energy_full" : "energy_empty";

    // Update button text
    this.repeatButton.setText(buttonText);

    // Get the container (which is the 'text' property of the button Label)
    const contentContainer = this.repeatButton.getElement(
      "text"
    ) as Phaser.GameObjects.Container;

    if ((contentContainer?.list?.length ?? 0) > 1) {
      const icon = contentContainer.list[1] as Phaser.GameObjects.Image;

      if (icon) {
        icon.setTexture(energyIconTexture);
      }
    }
  }
}
